import React, {
  useEffect,
  createContext,
  useState,
  useCallback,
  useMemo
} from 'react';
import PropTypes from 'prop-types';

import { gtagEvent } from 'src/utils/gtag';
import useRestApiClient from 'src/hooks/useRestApiClient';

const initialState = {
  myApprovals: [],
  isPreloaded: false,
  filteredMyApprovals: [],
  isLoading: false,
  filterValues: { search_title: '' },
  setFilterValues: () => Promise.resolve()
};

const MyApprovalsContext = createContext(initialState);

export const MyApprovalsProvider = (props) => {
  const { children } = props;
  const [filterValues, setFilterValues] = useState(initialState.filterValues);
  const [loading, setLoading] = useState(true);
  const [isPreloaded, setPreloaded] = useState(false);
  const [myApprovals, setMyApprovals] = useState(initialState.myApprovals);
  const [filteredMyApprovals, setFilteredMyApprovals] = useState(
    initialState.filteredMyApprovals
  );
  const client = useRestApiClient();

  const loadMyApprovals = async (force = false) => {
    if (myApprovals.length > 0 && !force) return;

    try {
      setLoading(true);
      const {
        data: { message }
      } = await client.get('/approvals');
      setMyApprovals(message);
      // setFilteredMyApprovals(message);
      setPreloaded(true);
    } catch (error) {
      setMyApprovals([]);
      if (error?.response?.status === 404) {
        setPreloaded(true);
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateMyApprovalLocal = useCallback(
    (approvalId, data) => {
      setMyApprovals((currentList) => {
        const newList = currentList.map((approval) => {
          return approval.id === approvalId
            ? { ...approval, ...data }
            : approval;
        });
        return [...newList];
      });
    },
    [myApprovals]
  );

  const getApprovalsByTemplate = (templateId) => {
    const controller = new AbortController();
    const request = client.get(
      `/approvals/template/${templateId}?cb=${+new Date()}`,
      {
        signal: controller.signal
      }
    );
    return { request, controller };
  };

  const getApprovalsByMobileApp = (mobileAppId) => {
    const controller = new AbortController();
    const request = client.get(
      `/approvals/app/${mobileAppId}?cb=${+new Date()}`,
      {
        signal: controller.signal
      }
    );
    return { request, controller };
  };

  const getApprovalsBySnippet = (snippetId) => {
    const controller = new AbortController();
    const request = client.get(
      `/approvals/snippet/${snippetId}?cb=${+new Date()}`,
      {
        signal: controller.signal
      }
    );
    return { request, controller };
  };

  const getApprovalsByFeature = (featureId) => {
    const controller = new AbortController();
    const request = client.get(
      `/approvals/feature/${featureId}?cb=${+new Date()}`,
      {
        signal: controller.signal
      }
    );
    return { request, controller };
  };

  const getApprovalsByAsset = (assetId) => {
    const controller = new AbortController();
    const request = client.get(
      `/approvals/asset/${assetId}?cb=${+new Date()}`,
      {
        signal: controller.signal
      }
    );
    return { request, controller };
  };

  useEffect(() => {
    let approvals = myApprovals;
    const { search_title: searchTitle } = filterValues;

    if (searchTitle) {
      gtagEvent('search', {
        search_term: searchTitle
      });

      approvals = approvals.filter(
        ({ subject }) =>
          subject &&
          subject
            .toString()
            .toLowerCase()
            .indexOf(searchTitle.toLowerCase()) > -1
      );
    }

    setFilteredMyApprovals([...approvals]);
  }, [myApprovals, filterValues]);

  const values = useMemo(
    () => ({
      loading,
      isPreloaded,
      filterValues,
      myApprovals,
      filteredMyApprovals,
      setFilterValues,
      loadMyApprovals,
      updateMyApprovalLocal,
      getApprovalsByTemplate,
      getApprovalsByMobileApp,
      getApprovalsBySnippet,
      getApprovalsByFeature,
      getApprovalsByAsset,
    }),
    [
      loading,
      filterValues,
      myApprovals,
      filteredMyApprovals,
      updateMyApprovalLocal
    ]
  );

  return (
    <MyApprovalsContext.Provider value={values} {...props}>
      {children}
    </MyApprovalsContext.Provider>
  );
};

MyApprovalsProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default MyApprovalsContext;
